import { cardAnatomy } from '@chakra-ui/anatomy';
import { createMultiStyleConfigHelpers } from '@chakra-ui/react';
import { cssVar, defineStyle } from '@chakra-ui/styled-system';

import { customColors } from '../../theme/customColors';
import { $groupSpacingX, $groupSpacingY } from '../Group/Group.style';

const { bg, border, text } = customColors;
const customParts = [
  'title',
  'headerControl',
  'section',
  'sectionControl',
  'sectionTitle',
] as const;
const { definePartsStyle, defineMultiStyleConfig } =
  createMultiStyleConfigHelpers([...cardAnatomy.keys, ...customParts]);

const $bg = cssVar('card-bg');
const $radius = cssVar('card-radius');
const $border = cssVar('card-border-width');
const $borderColor = cssVar('card-border-color');
const $outerPaddingY = cssVar('card-outer-padding-y');
const $outerPaddingX = cssVar('card-outer-padding-x');
// Allows content rendered with tables to be aligned properly
const $bodyPaddingX = cssVar('card-body-padding-x');
const $childCount = cssVar('child-count');

const baseStyle = definePartsStyle({
  header: {
    [$groupSpacingY.variable]: 'space.2',
    [$groupSpacingX.variable]: 'space.2',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: false,
    pt: $outerPaddingY.reference,
    pb: 4,
    px: $outerPaddingX.reference,
    '&:has(> *:nth-child(1))': {
      [$childCount.variable]: 1,
    },
    '&:has(> *:nth-child(2))': {
      [$childCount.variable]: 2,
    },
    '&:has(> *:nth-child(3))': {
      [$childCount.variable]: 3,
    },
    '&:has(> *:nth-child(4))': {
      [$childCount.variable]: 4,
    },
    '&:has(> *:nth-child(5))': {
      [$childCount.variable]: 5,
    },
    '&:last-child': {
      pb: $outerPaddingY.reference,
    },
    '[data-is-divided=true] > &:not(:last-child)': {
      borderBottom: '1px solid',
      borderBottomColor: border.primary,
    },
    '&:has(> .chakra-button__group, > .chakra-button, > .chakra-form-control, > .orbit-card__header-control)':
      {
        display: 'grid',
        gridColumnGap: 6,
        gridTemplateColumns: '1fr auto',
        '& > .chakra-button__group, & > .chakra-button, & > .chakra-form-control, & > .orbit-card__header-control':
          {
            gridRow: '1 / span calc(var(--child-count) - 1)',
            gridColumnStart: '2',
            alignSelf: 'start',
          },
        '& > :not(.chakra-button__group, .chakra-button, .chakra-form-control, .orbit-card__header-control)':
          {
            gridColumnStart: '1',
          },
        '& > .chakra-form-control': {
          flexDirection: 'row',
          alignItems: 'center',
          alignSelf: 'start',
        },
        '& .chakra-form__label': {
          fontSize: 'sm',
          lineHeight: 'sm',
          fontWeight: 'semibold',
        },
      },
  },
  headerControl: {
    display: 'flex',
    gap: 3,
    alignItems: 'center',
  },
  body: {
    [$bodyPaddingX.variable]: 0,
    color: text.primary,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: false,
    pt: 4,
    pb: 4,
    px: $outerPaddingX.reference,
    '&:last-child': {
      pb: $outerPaddingY.reference,
    },
    '&:has(> .chakra-table__container, > .chakra-table)': {
      [$bodyPaddingX.variable]: $outerPaddingX.reference,
      px: 0,
      overflowY: 'auto',
    },
    '&:has(> .chakra-table__container:first-child, > .chakra-table:first-child)':
      {
        pt: 0,
      },
    '&:has(> .chakra-table__container:last-child, > .chakra-table:last-child)':
      {
        pb: 0,
      },
    '&:first-of-type': {
      '&:has(.chakra-table__container)': {
        pt: 0,
      },
      pt: $outerPaddingY.reference,
    },
  },
  title: {
    display: 'flex',
    gap: 2,
    alignItems: 'center',
  },
  footer: {
    '[data-is-divided=true] > &:not(:first-child)': {
      borderTop: '1px solid',
      borderTopColor: border.primary,
    },
    '&:has(.orbit-pagination__container)': {
      p: 0,
    },
    '.chakra-card__body:has(.chakra-table__container:only-child, .chakra-table:only-child) + &:has(.orbit-pagination__container)':
      {
        borderTop: '1px solid',
        borderColor: border.primary,
      },
  },
  section: {
    [$groupSpacingY.variable]: 'space.1',
    [$groupSpacingX.variable]: 'space.2',
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    '&:has(> .orbit-group + .orbit-card__section-control)': {
      columnGap: 6,
    },
  },
  sectionControl: {
    alignSelf: 'start',
    display: 'flex',
    gap: 2,
    alignItems: 'center',
  },
  sectionTitle: {
    display: 'flex',
    gap: 2,
    alignItems: 'center',
  },
});

const tableOverflowContainer = defineStyle({
  '&:has(.chakra-card__body > .chakra-table__container, .chakra-card__body > .chakra-table)':
    {
      overflow: 'hidden',
    },
});

const tableBorderOverrides = defineStyle({
  '[data-is-divided=true] &:not(last-child) .chakra-table:last-child tr:last-child td':
    {
      borderBottom: 'none',
    },
  '&:last-child .chakra-table:last-child tr:last-child td': {
    borderBottom: 'none',
  },
  '&:has(+ .chakra-card__footer > .orbit-pagination__container) .chakra-table:last-child tr:last-child td':
    {
      borderBottom: 'none',
    },
});

const variantOutline = definePartsStyle({
  container: {
    [$bg.variable]: bg.primary,
    [$borderColor.variable]: border.primary,
    [$radius.variable]: '0.5rem',
    [$border.variable]: '1px',
    [$outerPaddingY.variable]: 'space.6',
    [$outerPaddingX.variable]: 'space.6',
    color: text.primary,
    boxShadow: 'cards',
    ...tableOverflowContainer,
  },
  body: {
    ...tableBorderOverrides,
  },
});

const variantFilled = definePartsStyle({
  container: {
    [$bg.variable]: bg.secondary,
    [$borderColor.variable]: border.primary,
    [$radius.variable]: '0.5rem',
    [$border.variable]: '1px',
    [$outerPaddingY.variable]: 'space.6',
    [$outerPaddingX.variable]: 'space.6',
    color: text.primary,
    ...tableOverflowContainer,
  },
  body: {
    ...tableBorderOverrides,
  },
});

const variantUnstyled = definePartsStyle({
  container: {
    [$bg.variable]: false,
    [$outerPaddingY.variable]: 'space.0',
    [$outerPaddingX.variable]: 'space.0',
    backgroundColor: false,
    boxShadow: false,
  },
});

const variantControl = definePartsStyle({
  container: {
    ...variantOutline.container,
    '&:has(> label[data-checked])': {
      borderColor: 'transparent',
      boxShadow: `0 0 0 2px ${border.selected}`,
    },
  },
});

export const CardStyles = defineMultiStyleConfig({
  baseStyle,
  variants: {
    outline: variantOutline,
    unstyled: variantUnstyled,
    filled: variantFilled,
    control: variantControl,
  },
  defaultProps: {
    variant: 'outline',
  },
});
